import type { StaffGrant } from "./useStaffGrant";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/browser";

// Imported ahead of fpFetch deliberately. `redux/store.ts` calls
// `readInitialUserDataFromCache()` at module scope, and fpFetch sits on a
// cycle (fpFetch -> handleError -> fpLog -> store -> here). Anything this
// module needs during that eager call has to be initialized before the cycle
// is entered, and the session accessor is one of them.
import {
  clearSessionToken,
  getSessionToken,
  hasInMemorySessionToken,
  subscribeSessionToken,
} from "@/services/sessionToken";
import {
  clearClientEnvironmentToken,
  getClientEnvironmentToken,
  isClientEnvironment,
} from "@/services/clientEnvironment";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { updateUserData } from "@/redux/userSlice";

// Key bumped when the persisted shape changes so stale caches don't hydrate
// into an incompatible Redux state.
//
// Exported because per-viewer client-side preferences have to be scoped to
// whoever is signed in (two colleagues share a browser profile more often
// than is comfortable), and this cache is the only synchronous answer to
// "who is that" available outside a React tree.
export const CURRENT_USER_CACHE_KEY = "fp_user_identity_v1";

const CACHE_KEY = CURRENT_USER_CACHE_KEY;
export const CURRENT_USER_QUERY_KEY = ["currentUser"] as const;

// Cross-tab logout signal written by LogOut.jsx.
const LOGOUT_STORAGE_KEY = "fp_logout";

export interface Permission {
  organizationId?: string;
  read?: boolean;
  write?: boolean;
  admin?: boolean;
  manager?: boolean;
}

export interface CurrentUser {
  user_id: string;
  email: string;
  name: string;
  permissions: Permission[];
  email_verified: boolean;
  isApproved: boolean;
  requiresSsoLogin: boolean;
  organization: { _id: string | null; name: string | null } | null;
  can_upload: boolean;
  can_edit_rms: boolean;
  // Finpilot staff (#10718): the one identity field the staff bit has. It
  // opens the console and the staff paths that open a grant (AuthWall's
  // misplaced-staff screen, logging out, the client environment), and gates
  // nothing else: inside an organization only the organization role counts.
  // `staffGrant` is a staff member's open client environment, reported on
  // their own account (which never moves). `clientEnvironment` is set only
  // inside the client environment, where this identity is the collaborator
  // the grant activated. Optional because a cached identity written before
  // these fields existed lacks them.
  isStaff?: boolean;
  staffGrant?: StaffGrant | null;
  clientEnvironment?: StaffGrant | null;
  // A staff account whose own row still sits in a client org has no org
  // until it is moved home (#10718 slice 1b).
  homeOrganizationMisplaced?: boolean;
  stytch_raw_user?: {
    providers?: Array<{ profile_picture_url?: string }>;
    [k: string]: unknown;
  };
  providers?: Array<{ profile_picture_url?: string }>;
}

/** Whether the user is the admin of the organization they act in: the org
 *  permission entry carries `admin: true`. */
export function isOrgAdmin(user: CurrentUser): boolean {
  return (
    user.permissions?.some(
      (p) => p.organizationId != null && p.admin === true
    ) ?? false
  );
}

/** The org admin's powers over the organization's own configuration (#10718
 *  slice 5): attribute designations, document types, address validation,
 *  folder sort. The org's admin alone (a staff member's collaborator holds
 *  it after the grant's admin elevation). Mirrors the backend's
 *  `administers_org()`. */
export function canAdministerOrg(user: CurrentUser): boolean {
  return isOrgAdmin(user);
}

function loadCachedUser(): CurrentUser | null {
  // Cached identity is meaningless without a session cookie. Skipping the
  // read here keeps `query.data` undefined when logged out, which prevents
  // the Redux-bridging effect from dispatching a stale isUserLoggedIn=true.
  if (!getSessionToken()) return null;
  // The cache is the home tab's identity, in storage a client tab shares
  // (#10718 slice 3): shown there, it would put the staff member's own org on
  // screen while requests act in the client.
  if (isClientEnvironment()) return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CurrentUser;
    if (!parsed?.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCached(user: CurrentUser): void {
  // A collaborator is a different account for every grant, and the cache
  // belongs to the home tab (#10718): a client tab neither writes nor clears
  // it.
  if (isClientEnvironment()) return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(user));
  } catch {
    // localStorage can throw (private browsing, quota). Losing the cache just
    // means the next load reverts to the pre-optimization behavior.
  }
}

/**
 * Who the browser last saw signed in, for scoping client-side preferences.
 *
 * Deliberately not gated on the session cookie the way `loadCachedUser` is:
 * this identifies *which* person's preference to read, not whether they are
 * authorized for anything. Losing the id on a cookie hiccup would silently
 * drop someone back to their browser's timezone, which is the failure this
 * exists to prevent.
 */
export function readCachedCurrentUserId(): string | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CurrentUser>;
    return parsed?.user_id ?? null;
  } catch {
    return null;
  }
}

export function clearCachedCurrentUser(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {}
}

const authSubscribers = new Set<() => void>();

export function notifyAuthChanged(): void {
  authSubscribers.forEach((cb) => cb());
}

export function useAuthSignal(): void {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((v) => v + 1);
    authSubscribers.add(cb);
    // An embedded frame receives its session by postMessage rather than by
    // cookie, so the token can appear with no navigation and no other signal.
    // Subscribing here is what re-renders the frame as authenticated (#11021).
    const unsubscribeToken = subscribeSessionToken(cb);
    return () => {
      authSubscribers.delete(cb);
      unsubscribeToken();
    };
  }, []);
}

function toReduxPayload(user: CurrentUser) {
  const profileImage =
    user.providers?.[0]?.profile_picture_url ??
    user.stytch_raw_user?.providers?.[0]?.profile_picture_url ??
    null;
  return {
    userId: user.user_id,
    userEmail: user.email,
    userName: user.name,
    isUserLoggedIn: true,
    profileImage,
    permissions: user.permissions,
    isApproved: user.isApproved,
    organization: user.organization,
    isEmailVerified: user.email_verified,
    requiresSsoLogin: user.requiresSsoLogin,
    can_upload: user.can_upload,
    can_edit_rms: user.can_edit_rms,
    isStaff: user.isStaff ?? false,
    staffGrant: user.staffGrant ?? null,
    clientEnvironment: user.clientEnvironment ?? null,
  };
}

// Called by the Redux store at construction time so userData is populated
// before any component renders. This is what makes the sidebar, org-scoped
// hooks, and /rms/bootstrap fire immediately instead of waiting on fp0052.
export function readInitialUserDataFromCache(): ReturnType<
  typeof toReduxPayload
> | null {
  const cached = loadCachedUser();
  if (!cached) return null;
  // Only hydrate if the user still has a session cookie — otherwise the
  // cached identity is meaningless and the login screen should show.
  if (!getSessionToken()) return null;
  return toReduxPayload(cached);
}

async function fetchCurrentUser(): Promise<CurrentUser> {
  const stytchSession = getSessionToken();
  if (!stytchSession) throw new Error("no stytch session");

  const response = await fpFetch(`${BASE_API_URL}/userData/fp0052`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      stytchSession,
      inviteCode: Cookies.get("fp_invite_code") || null,
    }),
  });

  if (!response?.ok) {
    // A 401 here means Stytch rejected this exact token — fp0052 re-validates
    // against Stytch rather than trusting any cache. If the token came from the
    // embed handshake, drop it.
    //
    // Without this, a dead handed-over token persists in storage and,
    // because memory deliberately wins over the cookie, permanently shadows a
    // perfectly good first-party cookie. The frame then 401s forever with no
    // way to recover short of clearing storage by hand — which is exactly what
    // happened once the token started surviving reloads (#11021).
    if (response?.status === 401 && hasInMemorySessionToken()) {
      clearSessionToken();
    }
    // Inside the client environment a 401 means the grant ended (released,
    // expired, replaced): drop the collaborator token so the app shows that
    // access ended instead of retrying a dead credential (#10718). Only the
    // token this read sent: the entry page may have stored a fresh one while
    // it was in flight.
    if (
      response?.status === 401 &&
      isClientEnvironment() &&
      getClientEnvironmentToken() === stytchSession
    ) {
      clearClientEnvironmentToken();
    }
    // `||`, not `??`: a request that never reached the server reports status 0,
    // and "fp0052 failed: 0" says less than "no response" does (#11634).
    throw new Error(`fp0052 failed: ${response?.status || "no response"}`);
  }

  const data = (await response.json()) as CurrentUser;
  if (!data?.email) throw new Error("fp0052 returned no email");
  return data;
}

export interface UseCurrentUserResult {
  data: CurrentUser | undefined;
  // True only when we have no cached identity AND the fetch is in flight.
  // Consumers should render a skeleton only in this state.
  isBootstrapping: boolean;
  isFetching: boolean;
  error: Error | null;
  hasCachedIdentity: boolean;
  refetch: () => void;
}

export function useCurrentUser(): UseCurrentUserResult {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const cached = loadCachedUser();
  const hasCookie = !!getSessionToken();

  const query = useQuery<CurrentUser, Error>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchCurrentUser,
    initialData: cached ?? undefined,
    // Even when seeded from cache, always refetch on mount — cached identity
    // may be stale (permissions changed, org switched, etc.).
    staleTime: 0,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: true,
    enabled: hasCookie,
  });

  // Persist fresh data and bridge into Redux for the ~60 existing consumers
  // that read from state.userData.
  useEffect(() => {
    if (!query.data) return;
    saveCached(query.data);
    dispatch(updateUserData(toReduxPayload(query.data)));
    Sentry.setUser({ email: query.data.email });
  }, [query.data, dispatch]);

  // Cross-tab logout: another tab wrote to fp_logout — drop the cache and
  // query so the next render sees no identity.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // Not in a client tab: see main.jsx (#10718).
      if (e.key === LOGOUT_STORAGE_KEY && !isClientEnvironment()) {
        clearCachedCurrentUser();
        queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [queryClient]);

  return {
    data: query.data,
    isBootstrapping: !cached && query.isLoading && hasCookie,
    isFetching: query.isFetching,
    error: (query.error as Error | null) ?? null,
    hasCachedIdentity: !!cached,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * The signed-in user as the app already knows it, for components that only
 * read who is signed in (#12239). The sign-in gate's `useCurrentUser` owns
 * the read: it refetches on mount and on focus, persists the answer and
 * bridges it into Redux. This observer shares its query without refetching on
 * mount or focus, so mounting it issues no read of its own once the gate has
 * an answer, and has no side effects. It still re-renders on every update to
 * the query, including an invalidation's refetch. With no answer cached yet
 * it reads once itself.
 */
export function useCurrentUserSnapshot(): { data: CurrentUser | undefined } {
  const query = useQuery<CurrentUser, Error>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: fetchCurrentUser,
    enabled: !!getSessionToken(),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    retry: false,
  });
  return { data: query.data };
}
